import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircle, AlertCircle, Clock, Send, Share2, Facebook, Twitter, MessageCircle, Link } from 'lucide-react';
import { useFillTimer } from '../hooks/useFillTimer';
import { telaBrasilAPI, telaBrasilStorage } from '../utils/telaBrasilAPI';
import ResultadoEnquete from './ResultadoEnquete';
import SubheaderVoltar from './common/SubheaderVoltar';
import FooterPublico from './common/FooterPublico';

const FormularioTelaBrasil = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [trackingToken, setTrackingToken] = useState(null);
  const [enqueteEncerrada, setEnqueteEncerrada] = useState(false);
  const [estatisticas, setEstatisticas] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [canVote, setCanVote] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const retryCountRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const MAX_RETRIES = 3;

  const { register, handleSubmit, formState: { errors } } = useForm();
  const { startTimer, stopTimer, getCurrentTime, formatTime, isTimerActive } = useFillTimer();

  // Função robusta para carregar estatísticas com retry e circuit breaker.
  // Usa refs (não state) para o contador de tentativas: assim a função
  // mantém identidade estável entre renders e pode entrar nas dependências
  // dos useEffect abaixo sem disparar polling/reload em duplicidade.
  const carregarEstatisticas = useCallback(async (isRetry = false) => {
    // Circuit breaker: para após MAX_RETRIES tentativas
    if (retryCountRef.current >= MAX_RETRIES && !isRetry) {
      console.error('❌ Circuit breaker ativado: máximo de tentativas atingido');
      setStatsError('Não foi possível carregar as estatísticas. Tente recarregar a página.');
      return;
    }

    try {
      setLoadingStats(true);
      setStatsError(null);

      console.log(`📊 Carregando estatísticas... (tentativa ${retryCountRef.current + 1}/${MAX_RETRIES})`);

      const stats = await telaBrasilAPI.obterEstatisticas();

      if (!stats || !stats.locais || stats.locais.length === 0) {
        throw new Error('Estatísticas vazias ou inválidas');
      }

      console.log('✅ Estatísticas carregadas com sucesso:', stats);
      setEstatisticas(stats);
      retryCountRef.current = 0; // Reset contador em caso de sucesso

    } catch (error) {
      console.error(`❌ Erro ao carregar estatísticas (tentativa ${retryCountRef.current + 1}):`, error);

      retryCountRef.current += 1;

      // Retry exponencial: 2s, 4s, 8s
      if (retryCountRef.current < MAX_RETRIES) {
        const retryDelay = Math.pow(2, retryCountRef.current - 1) * 1000;
        console.log(`🔄 Tentando novamente em ${retryDelay/1000}s...`);

        setTimeout(() => {
          carregarEstatisticas(true);
        }, retryDelay);
      } else {
        setStatsError('Erro ao carregar estatísticas. Verifique sua conexão.');
      }
    } finally {
      setLoadingStats(false);
    }
  }, [MAX_RETRIES]);

  // Verifica se a enquete está encerrada
  useEffect(() => {
    const dataEncerramento = new Date('2026-07-01T23:55:00');
    const agora = new Date();

    if (agora > dataEncerramento) {
      setEnqueteEncerrada(true);
      carregarEstatisticas(); // Carrega estatísticas para mostrar resultado final
    }
  }, [carregarEstatisticas]);

  // Verifica se já votou ao carregar
  useEffect(() => {
    if (telaBrasilStorage.hasVoted()) {
      setIsSubmitted(true);
      carregarEstatisticas();
    }
  }, [carregarEstatisticas]);

  // Polling inteligente: apenas durante enquete ativa
  useEffect(() => {
    const dataEncerramento = new Date('2026-07-01T23:55:00');
    const agora = new Date();
    const enqueteAtiva = agora <= dataEncerramento;

    // Só faz polling se enquete ativa E usuário já votou
    if (enqueteAtiva && isSubmitted && !pollingIntervalRef.current) {
      console.log('🔄 Iniciando polling de estatísticas (5s)');

      // Carrega imediatamente
      carregarEstatisticas();

      // Inicia polling de 5 em 5 segundos
      pollingIntervalRef.current = setInterval(() => {
        carregarEstatisticas();
      }, 5000);
    }

    // Cleanup ao desmontar ou quando enquete encerrar
    return () => {
      if (pollingIntervalRef.current) {
        console.log('🛑 Parando polling de estatísticas');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isSubmitted, enqueteEncerrada, carregarEstatisticas]);

  // Gera token e inicia timer quando o componente carrega
  useEffect(() => {
    const initializeToken = async () => {
      try {
        let token = telaBrasilStorage.getTrackingToken();
        
        if (!token) {
          const response = await telaBrasilAPI.gerarToken();
          token = response.token;
          telaBrasilStorage.saveTrackingToken(token);
        }
        
        setTrackingToken(token);
        startTimer();
        
        // Mostra modal de distração por 2 segundos
        setShowModal(true);
        setTimeout(() => {
          setShowModal(false);
          setCanVote(true);
        }, 2000);
      } catch (error) {
        console.error('Erro ao inicializar token:', error);
        toast.error('Erro ao inicializar sistema de segurança');
      }
    };

    initializeToken();
  }, [startTimer]);

  // Submete o formulário
  const onSubmit = async (data) => {
    if (isSubmitting) return;
    
    // Verifica se já pode votar (passou os 2 segundos)
    if (!canVote) {
      toast.error('Aguarde um momento antes de votar...');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const fillTime = stopTimer();
      
      const votoData = {
        local_escolhido: data.local_escolhido,
        tempo_preenchimento_ms: fillTime,
        token_rastreamento: trackingToken,
        website_url: data.website_url || ''
      };

      const response = await telaBrasilAPI.registrarVoto(votoData);
      
      if (response.success) {
        telaBrasilStorage.markAsVoted();
        telaBrasilStorage.clearTrackingToken();
        setIsSubmitted(true);
        await carregarEstatisticas();
        toast.success('Voto registrado com sucesso! Obrigado pela participação.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

    } catch (error) {
      console.error('Erro ao enviar voto:', error);
      
      // Se erro 409 (já votou), trata como sucesso e mostra tela de agradecimento
      if (error.response && error.response.status === 409) {
        telaBrasilStorage.markAsVoted();
        telaBrasilStorage.clearTrackingToken();
        setIsSubmitted(true);
        await carregarEstatisticas();
        toast.success('Obrigado! Seu voto já foi registrado anteriormente.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (error.response && error.response.status === 429) {
        // Erro 429 - Rate limit excedido
        toast.error('Aguarde alguns segundos antes de tentar novamente.', {
          duration: 5000
        });
      } else {
        const message = error.userMessage || 'Erro ao registrar voto. Tente novamente.';
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se a enquete está encerrada
  if (enqueteEncerrada) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
        <Toaster position="top-center" />

        <header className="bg-primary text-white shadow-lg">
          <div className="container-responsive py-6">
            <div className="flex items-center justify-center lg:justify-between">
              <a href="https://corumba.ms.gov.br/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/assets/logo_prefeitura_horizontal.png"
                  alt="Prefeitura de Corumbá"
                  className="h-12 w-auto hover:opacity-90 transition-opacity"
                />
              </a>
              <img
                src="/assets/background_corumba.png"
                alt="Corumbá"
                className="hidden lg:block h-12 w-auto"
              />
            </div>
          </div>
        </header>
        <SubheaderVoltar />

        <main className="container-responsive py-12">
          <div className="max-w-5xl mx-auto">
            {/* Mensagem de encerramento */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-red-50 border-2 border-red-200 px-6 py-3 rounded-full mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <span className="font-bold text-red-900">Enquete Encerrada</span>
              </div>
              <p className="text-gray-600">
                Encerrado em <strong>01 de Julho de 2026 às 23:55</strong>
              </p>
            </div>

            {/* Componente de Resultados */}
            <ResultadoEnquete
              locais={estatisticas?.locais || []}
              totalVotos={estatisticas?.total || 0}
              dataEncerramento="2026-07-01T23:55:00"
              titulo="🏆 Resultado Final - Telão do Brasil"
              descricao="Confira o local escolhido pela população de Corumbá para assistir ao jogo do Brasil na Copa 2026!"
              loading={loadingStats}
              error={statsError}
              onRetry={() => {
                retryCountRef.current = 0;
                carregarEstatisticas();
              }}
            />
          </div>
        </main>
        <FooterPublico />
      </div>
    );
  }

  // Se já votou
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <Toaster position="top-center" />

        <header className="bg-primary text-white shadow-lg">
          <div className="container-responsive py-6">
            <div className="flex items-center justify-center lg:justify-between">
              <a href="https://corumba.ms.gov.br/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/assets/logo_prefeitura_horizontal.png"
                  alt="Prefeitura de Corumbá"
                  className="h-12 w-auto hover:opacity-90 transition-opacity"
                />
              </a>
              <img
                src="/assets/background_corumba.png"
                alt="Corumbá"
                className="hidden lg:block h-12 w-auto"
              />
            </div>
          </div>
        </header>
        <SubheaderVoltar />

        <main className="container-responsive py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card animate-fade-in-up">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Obrigado pela sua participação!
              </h1>
              
              <p className="text-lg text-gray-600 mb-6">
                Seu voto foi registrado com sucesso. O local escolhido pela maioria receberá o telão oficial para assistir ao jogo do Brasil!
              </p>
              
              <div className="bg-green-50 p-4 rounded-lg mb-6">
                <p className="text-green-700 font-medium">
                  🇧🇷 Jogo do Brasil: 05/07/2026 às 16h ⚽
                </p>
              </div>

              {estatisticas && estatisticas.locais && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    📊 Ranking dos Locais Mais Votados
                  </h3>
                  <div className="space-y-3">
                    {estatisticas.locais
                      .sort((a, b) => b.votos - a.votos)
                      .map((local, index) => {
                        const porcentagem = estatisticas.total > 0 
                          ? ((local.votos / estatisticas.total) * 100).toFixed(1)
                          : 0;
                        const cores = [
                          'bg-green-600',
                          'bg-yellow-500',
                          'bg-blue-500',
                          'bg-purple-500',
                          'bg-pink-500',
                          'bg-indigo-500'
                        ];
                        const cor = cores[index] || 'bg-gray-500';
                        
                        return (
                          <div key={local.local} className="text-left">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-700">
                                {index + 1}º - {local.nome}
                              </span>
                              <span className="text-sm font-bold text-gray-900">
                                {local.votos} votos ({porcentagem}%)
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                              <div
                                className={`${cor} h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                                style={{ width: `${porcentagem}%` }}
                              >
                                {parseFloat(porcentagem) > 10 && (
                                  <span className="text-xs font-bold text-white">
                                    {porcentagem}%
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Total de votos: {estatisticas.total}
                  </p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-8">
                <div className="flex items-center justify-center mb-4">
                  <Share2 className="w-6 h-6 text-primary-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Compartilhe esta enquete
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-6">
                  Convide mais pessoas a participarem!
                </p>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const text = encodeURIComponent('⚽ VOTE NO LOCAL DO TELÃO! 🇧🇷\n\nAjude a escolher onde assistir ao jogo do Brasil na Copa 2026 em Corumbá-MS! �\n\nSua opinião conta! 💚💛');
                      window.open(`https://wa.me/?text=${text}%0A%0A${url}`, '_blank');
                    }}
                    className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </button>

                  <button
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const quote = encodeURIComponent('⚽ Vote no local do telão para assistir ao jogo do Brasil na Copa 2026! 🇧🇷 Sua opinião conta! 💚💛');
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank');
                    }}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Facebook className="w-5 h-5" />
                    <span className="hidden sm:inline">Facebook</span>
                  </button>

                  <button
                    onClick={() => {
                      const url = encodeURIComponent(window.location.href);
                      const text = encodeURIComponent('⚽ VOTE NO LOCAL DO TELÃO! 🇧🇷\n\nEscolha onde assistir ao jogo do Brasil na Copa 2026 em Corumbá-MS! �💚💛\n\n#Corumbá #Copa2026 #VaiBrasil');
                      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                    }}
                    className="flex items-center space-x-2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Twitter className="w-5 h-5" />
                    <span className="hidden sm:inline">Twitter</span>
                  </button>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href).then(() => {
                        toast.success('Link copiado!');
                      }).catch(() => {
                        toast.error('Erro ao copiar link');
                      });
                    }}
                    className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                  >
                    <Link className="w-5 h-5" />
                    <span className="hidden sm:inline">Copiar Link</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
        <FooterPublico />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50">
      <Toaster position="top-center" />
      
      {/* Modal de Distração */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl animate-scale-in">
            <div className="mb-4">
              <span className="text-6xl">⚽🇧🇷</span>
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-3">
              Preparando sua votação...
            </h2>
            <p className="text-gray-700 text-lg mb-4">
              Sua opinião é muito importante para escolher onde assistir ao jogo do Brasil!
            </p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          </div>
        </div>
      )}
      
      <header className="bg-primary text-white shadow-lg">
        <div className="container-responsive py-4">
          {/* Layout Mobile: Vertical (brasão + timer) */}
          <div className="lg:hidden flex flex-col items-center space-y-2">
            <a href="https://corumba.ms.gov.br/" target="_blank" rel="noopener noreferrer">
              <img
                src="/assets/logo_prefeitura_horizontal.png"
                alt="Prefeitura de Corumbá"
                className="h-10 w-auto hover:opacity-90 transition-opacity"
              />
            </a>
            {isTimerActive && (
              <div className="flex items-center space-x-1 text-primary-100">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {formatTime(getCurrentTime())}
                </span>
              </div>
            )}
          </div>

          {/* Layout Desktop: Horizontal (brasão | timer | imagem) */}
          <div className="hidden lg:flex relative items-center justify-between">
            <a href="https://corumba.ms.gov.br/" target="_blank" rel="noopener noreferrer">
              <img
                src="/assets/logo_prefeitura_horizontal.png"
                alt="Prefeitura de Corumbá"
                className="h-12 w-auto hover:opacity-90 transition-opacity"
              />
            </a>

            {isTimerActive && (
              <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center space-x-1 text-primary-100">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  {formatTime(getCurrentTime())}
                </span>
              </div>
            )}

            <img
              src="/assets/background_corumba.png"
              alt="Corumbá"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>
      <SubheaderVoltar />

      <main className="container-responsive py-8">
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
            ⚽ Telão do Brasil - Copa 2026 🇧🇷
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 mb-4 font-semibold">
            O Brasil entra em campo, e você escolhe onde a torcida vai se reunir!
          </p>
          <p className="text-base sm:text-lg text-gray-600 mb-2">
            Vote em apenas um local para receber o telão oficial da Prefeitura de Corumbá e venha viver essa emoção com toda a cidade.
          </p>
          <div className="bg-green-100 border-2 border-green-500 p-4 rounded-lg inline-block mt-4">
            <p className="text-xl font-bold text-green-800">
              🗓️ Jogo: 05/07/2026, às 16h ⚽💚💛
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto animate-fade-in-up">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            <input
              {...register('website_url')}
              type="text"
              className="honeypot"
              tabIndex="-1"
              autoComplete="off"
            />

            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Escolha o local para o telão oficial:
              </h3>
              
              <div className="space-y-3">
                {[
                  { value: 'praca_nova_corumba', label: '🏛️ Praça da Nova Corumbá' },
                  { value: 'porto_geral', label: '⚓ Porto Geral' },
                  { value: 'esplanada_nob', label: '🚂 Esplanada da NOB' },
                  { value: 'praca_cravo_vermelho', label: '🌹 Praça do Cravo Vermelho' },
                  { value: 'praca_generoso', label: '🌳 Bairro do Generoso' },
                  { value: 'praca_ceu', label: '⭐ Praça CEU' }
                ].map((option) => (
                  <label key={option.value} className="radio-option cursor-pointer hover:bg-green-50 transition-colors">
                    <input
                      {...register('local_escolhido', { required: 'Você deve escolher um local' })}
                      type="radio"
                      value={option.value}
                      className="h-5 w-5 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-gray-700 text-lg">{option.label}</span>
                  </label>
                ))}
              </div>
              
              {errors.local_escolhido && (
                <p className="mt-4 text-sm text-red-600 flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.local_escolhido.message}
                </p>
              )}
            </div>

            <div className="card text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Enviar Voto</span>
                  </>
                )}
              </button>
              
              <p className="mt-4 text-sm text-gray-500">
                Ao enviar, você confirma sua escolha. Vote apenas uma vez!
              </p>
            </div>
          </form>
        </div>
      </main>
      <FooterPublico />
    </div>
  );
};

export default FormularioTelaBrasil;
